import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  HcpCategoryListDto,
  HcpServiceListDto,
  HousecallProStatusDto,
  IrrigationPartListDto,
} from '@lib/dto/housecall-pro.dto'
import type { IrrigationPartKind } from '@lib/enums/irrigation-part-kind.enum'
import type {
  SetHcpWritesInput,
  UpdateHousecallProInput,
} from '@lib/input/housecall-pro.input'
import { housecallProService } from '@/services/HousecallProService'
import { housecallProKeys, proposalKeys } from '@/hooks/queryKeys'

/** The connection, the chosen category, and how fresh the copy is. */
export function useHousecallProStatus(): UseQueryResult<HousecallProStatusDto> {
  return useQuery({
    queryKey: housecallProKeys.status(),
    queryFn: () => housecallProService.getStatus(),
  })
}

/**
 * HCP's top-level categories, read live from HCP.
 *
 * Only asked for once the connection is known to exist, and not retried: a
 * refused key should say so once rather than three times.
 */
export function useHcpTopCategories(
  enabled: boolean,
): UseQueryResult<HcpCategoryListDto> {
  return useQuery({
    queryKey: housecallProKeys.topCategories(),
    queryFn: () => housecallProService.listTopCategories(),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  })
}

/** The services in the copy, for the labor and drive-charge settings. */
export function useHcpServices(
  enabled: boolean,
): UseQueryResult<HcpServiceListDto> {
  return useQuery({
    queryKey: housecallProKeys.services(),
    queryFn: () => housecallProService.listServices(),
    enabled,
    staleTime: 10 * 60_000,
  })
}

/**
 * What one part picker offers.
 *
 * A long `staleTime`: the copy changes when somebody syncs, and the API
 * refreshes a stale copy on its own when a picker reads it.
 */
export function useIrrigationParts(
  kind?: IrrigationPartKind,
): UseQueryResult<IrrigationPartListDto> {
  return useQuery({
    queryKey: housecallProKeys.parts(kind ?? null),
    queryFn: () => housecallProService.listParts(kind),
    staleTime: 10 * 60_000,
  })
}

/** Seeds the status from the response and drops every list built from the old copy. */
function useApplyStatus(): (status: HousecallProStatusDto) => void {
  const queryClient = useQueryClient()
  return (status) => {
    queryClient.setQueryData(housecallProKeys.status(), status)
    void queryClient.invalidateQueries({
      queryKey: [...housecallProKeys.all, 'parts'],
    })
    void queryClient.invalidateQueries({
      queryKey: housecallProKeys.services(),
    })
    // The category list is cached for five minutes. A different account's
    // categories would otherwise stay on a screen left open.
    void queryClient.invalidateQueries({
      queryKey: housecallProKeys.topCategories(),
    })
  }
}

export function useUpdateHousecallPro(): UseMutationResult<
  HousecallProStatusDto,
  Error,
  UpdateHousecallProInput
> {
  const applyStatus = useApplyStatus()
  return useMutation({
    mutationFn: (input: UpdateHousecallProInput) =>
      housecallProService.update(input),
    onSuccess: applyStatus,
  })
}

export function useSyncHousecallPro(): UseMutationResult<
  HousecallProStatusDto,
  Error,
  void
> {
  const applyStatus = useApplyStatus()
  return useMutation({
    mutationFn: () => housecallProService.sync(),
    onSuccess: applyStatus,
  })
}

/**
 * Switch creating things in HCP on or off. Every accepted proposal's job
 * panel says whether it can create, so those are asked again too.
 */
export function useSetHcpWrites(): UseMutationResult<
  HousecallProStatusDto,
  Error,
  SetHcpWritesInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetHcpWritesInput) =>
      housecallProService.setWrites(input),
    onSuccess: (status) => {
      queryClient.setQueryData(housecallProKeys.status(), status)
      void queryClient.invalidateQueries({
        queryKey: [...proposalKeys.all, 'hcp-jobs'],
      })
    },
  })
}
